import {
    Building2,
    BriefcaseBusiness,
    Calculator,
    ChartColumn,
    Coins,
    CreditCard,
    FileText,
    Handshake,
    House,
    Inbox,
    NotebookPen,
    RefreshCw,
    Settings,
    ShieldAlert,
    UserCheck,
    UserCog,
    type LucideIcon,
} from 'lucide-react';

import type { Persona } from '@/lib/hooks/useMe';
import { HOME, ROUTE_ACCESS, type RotaPagina } from '@/route-access';

import type { Badges } from '@alianca/core/contracts/identidade';

export interface NavItemDef {
    label: string;
    href: RotaPagina;
    icon: LucideIcon;
    /** Prefixos de rota em que o item fica ativo; vence o prefixo mais longo entre todos os itens. */
    match: readonly string[];
    /** Contador vermelho: só o que exige ação agora. */
    badge?: (b: Badges) => number;
    /** Total neutro, em `label-tertiary`. */
    count?: (b: Badges) => number;
}

export interface NavSectionDef {
    title?: string;
    items: readonly NavItemDef[];
}

/** Ordem da sidebar (docs/DESIGN-SYSTEM.md › Layout). Solar e Eletropostos são abas dentro das páginas, não grupos. */
export const NAV_SECTIONS: readonly NavSectionDef[] = [
    {
        items: [
            { label: 'Início', href: HOME, icon: House, match: [HOME] },
            { label: 'Cotação', href: '/contratos/cotacao', icon: Calculator, match: ['/contratos/cotacao', '/eletropostos/cotacao'] },
            { label: 'Propostas', href: '/propostas', icon: NotebookPen, match: ['/propostas', '/eletropostos/propostas'] },
            { label: 'Contratos', href: '/contratos', icon: FileText, match: ['/contratos', '/eletropostos/contratos'] },
        ],
    },
    {
        title: 'Operação',
        items: [
            { label: 'Solicitações', href: '/solicitacoes', icon: Inbox, match: ['/solicitacoes'], badge: (b) => b.solicitacoesPendentes },
            { label: 'Renovações', href: '/renovacoes', icon: RefreshCw, match: ['/renovacoes'], count: (b) => b.renovacoesPendentes },
            { label: 'Sinistros', href: '/sinistros', icon: ShieldAlert, match: ['/sinistros'], badge: (b) => b.sinistrosPendentes },
            { label: 'Chargebacks', href: '/chargebacks', icon: CreditCard, match: ['/chargebacks'], badge: (b) => b.chargebacksAbertos },
        ],
    },
    {
        title: 'Gestão',
        items: [
            { label: 'Relatórios', href: '/relatorios/financeiro', icon: ChartColumn, match: ['/relatorios/financeiro', '/relatorios/renovacoes'] },
            { label: 'Comissões', href: '/comissoes', icon: Coins, match: ['/comissoes'] },
            { label: 'Indicadores', href: '/indicadores', icon: UserCheck, match: ['/indicadores'] },
            { label: 'Parceiros', href: '/parceiros', icon: Handshake, match: ['/parceiros'] },
            { label: 'Carteira', href: '/carteira', icon: BriefcaseBusiness, match: ['/carteira'] },
            { label: 'Usuários', href: '/usuarios', icon: UserCog, match: ['/usuarios'] },
            { label: 'Minha empresa', href: '/minha-empresa', icon: Building2, match: ['/minha-empresa'] },
        ],
    },
];

export const NAV_FOOTER: readonly NavItemDef[] = [
    { label: 'Configurações', href: '/configuracoes', icon: Settings, match: ['/configuracoes'] },
];

/** Visibilidade vem da matriz de acesso das rotas: a sidebar não repete regra de persona. */
export function podeVer(item: NavItemDef, persona: Persona): boolean {
    const access = ROUTE_ACCESS[item.href];
    return 'publica' in access || access.personas.includes(persona);
}

export function secoesVisiveis(persona: Persona): NavSectionDef[] {
    return NAV_SECTIONS.map((s) => ({ ...s, items: s.items.filter((i) => podeVer(i, persona)) })).filter((s) => s.items.length > 0);
}

/** Item ativo: o de prefixo mais longo que casa com a rota (Cotação vence Contratos em `/contratos/cotacao`). */
export function itemAtivo(pathname: string, items: readonly NavItemDef[]): NavItemDef | undefined {
    let melhor: { item: NavItemDef; tamanho: number } | undefined;
    for (const item of items) {
        for (const prefixo of item.match) {
            const casa = pathname === prefixo || pathname.startsWith(`${prefixo}/`);
            if (casa && prefixo.length > (melhor?.tamanho ?? -1)) melhor = { item, tamanho: prefixo.length };
        }
    }
    return melhor?.item;
}
